import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyAgamMode({ brand }: ModeRendererProps) {
  // Dynamic perception; interactive identity
  // Lenticular stripes, polychrome grids, view-dependent art
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Lenticular stripe pattern - view-dependent */}
      {Array.from({ length: 80 }).map((_, i) => {
        const x = 60 + i * 8.5;
        const colors = [brand.aqua, brand.violet, brand.amber, brand.deep];
        const colorIndex = i % 4;
        const height = 240 + Math.sin(i * 0.3) * 40;
        
        return (
          <rect
            key={`lenticular-${i}`}
            x={x}
            y={80 + (280 - height) / 2}
            width={7}
            height={height}
            fill={colors[colorIndex]}
            opacity={0.7}
          />
        );
      })}
      
      {/* Agamograph effect - alternating stripes reveal different images */}
      {Array.from({ length: 60 }).map((_, i) => {
        const x = 80 + i * 10.5;
        const y1 = 400;
        const y2 = 580;
        
        // Image A - vertical gradient
        return (
          <g key={`agam-${i}`}>
            <rect
              x={x}
              y={y1}
              width={5}
              height={y2 - y1}
              fill={i % 2 === 0 ? brand.aqua : brand.violet}
              opacity={0.7}
            />
            <rect
              x={x + 5}
              y={y1}
              width={5}
              height={y2 - y1}
              fill={i % 2 === 0 ? brand.amber : brand.deep}
              opacity={0.6}
            />
          </g>
        );
      })}
      
      {/* Polychrome grid - Agam's color system */}
      {Array.from({ length: 12 }).map((_, row) =>
        Array.from({ length: 16 }).map((_, col) => {
          const x = 80 + col * 40;
          const y = 660 + row * 35;
          const colorCombos = [
            [brand.aqua, brand.violet],
            [brand.violet, brand.amber],
            [brand.amber, brand.aqua],
            [brand.deep, brand.aqua],
          ];
          const combo = colorCombos[(row + col) % 4];
          
          return (
            <g key={`poly-${row}-${col}`}>
              <rect
                x={x}
                y={y}
                width={18}
                height={32}
                fill={combo[0]}
                opacity={0.7}
              />
              <rect
                x={x + 18}
                y={y}
                width={18}
                height={32}
                fill={combo[1]}
                opacity={0.7}
              />
            </g>
          );
        })
      )}
      
      {/* Transformable pattern - changes with perspective */}
      {Array.from({ length: 40 }).map((_, i) => {
        const angle = (i / 40) * Math.PI * 2;
        const cx = 200;
        const cy = 200;
        const r1 = 60;
        const r2 = 110;
        
        // Radial segments
        const x1 = cx + Math.cos(angle) * r1;
        const y1 = cy + Math.sin(angle) * r1;
        const x2 = cx + Math.cos(angle) * r2;
        const y2 = cy + Math.sin(angle) * r2;
        
        const colors = [brand.aqua, brand.violet, brand.amber];
        const color = colors[i % 3];
        
        return (
          <line
            key={`radial-segment-${i}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth="3"
            opacity={0.6}
          />
        );
      })}
      
      {/* Kinetic optical pattern - Vasarely meets Agam */}
      {Array.from({ length: 10 }).map((_, i) => {
        const size = 180 - i * 16;
        const offset = i * 8;
        const rotation = i * 6;
        
        return (
          <rect
            key={`kinetic-${i}`}
            x={110 + offset}
            y={110 + offset}
            width={size}
            height={size}
            fill="none"
            stroke={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
            strokeWidth="2"
            opacity={0.4}
            transform={`rotate(${rotation} 200 200)`}
          />
        );
      })}
      
      {/* View-dependent stripes - interactive element suggestion */}
      {Array.from({ length: 30 }).map((_, i) => {
        const y = 1040 + i * 2.5;
        const phase = i * 0.3;
        
        const path = Array.from({ length: 60 }).map((_, j) => {
          const x = 80 + j * 10.5;
          const py = y + Math.sin(j * 0.4 + phase) * 8;
          return `${j === 0 ? "M" : "L"} ${x},${py}`;
        }).join(" ");
        
        return (
          <path
            key={`wave-stripe-${i}`}
            d={path}
            fill="none"
            stroke={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
            strokeWidth="1.5"
            opacity={0.5}
          />
        );
      })}
      
      {/* Dynamic identity markers */}
      {[
        { x: 550, y: 180, label: "View A" },
        { x: 550, y: 500, label: "View B" },
        { x: 550, y: 820, label: "View C" },
      ].map((marker, i) => (
        <g key={`marker-${i}`}>
          <circle cx={marker.x} cy={marker.y} r="40" fill="none" stroke={brand.deep} strokeWidth="2" opacity={0.4} />
          <circle cx={marker.x} cy={marker.y} r="30" fill="none" stroke={brand.violet} strokeWidth="1.5" opacity={0.5} />
          <circle cx={marker.x} cy={marker.y} r="20" fill="none" stroke={brand.aqua} strokeWidth="1" opacity={0.6} />
          <text
            x={marker.x}
            y={marker.y + 5}
            textAnchor="middle"
            fontFamily="sans-serif"
            fontSize="10"
            fill={brand.deep}
            opacity={0.6}
          >
            {marker.label}
          </text>
        </g>
      ))}
    </g>
  );
}
